import { QueryClient } from '@tanstack/react-query';

import { IPublicDesktopV2OffersGetOffersByPinResponse } from 'shared/repositories/commercial-listing-map/desktop/v2/offers/get-offers-by-pin';
import { IPublicDesktopV2OffersGetOffersResponse } from 'shared/repositories/commercial-search-offers/desktop/v2/offers/get-offers';
import { IFastFiltersUserResponseSchema83 } from 'shared/repositories/monolith-python/entities/schemas/FastFiltersUserResponseSchema83';
import { IClientSideState } from 'shared/services/ClientSideState';
import {
  completeJsonQueryByPinSelector,
  completeJsonQuerySelector,
} from 'shared/store/clientSide/slices/jsonQuery/selectors/smart';
import { listingLayoutIsShortSelector } from 'shared/store/clientSide/slices/listingLayout';
import { FetchGetOffersLoadService } from 'shared/store/serverSide/commercial-search-offers/desktop/v2/offers/get-offers/load';
import { FetchGetUserLoadService } from 'shared/store/serverSide/monolith-python/cian-api/site/v1/get-user-data-by-request/load';
import { userCianApiSiteV1GetUserDataByRequestSelector } from 'shared/store/serverSide/monolith-python/cian-api/site/v1/get-user-data-by-request/selectors';
import { IApplicationContext } from 'shared/types/ApplicationContext';
import { CianAnalyticsPageViewObject } from 'shared/types/analytics/CianAnalyticsPageViewObject';

import { AnalyticsPage, getAnalyticsPage } from './getAnalyticsPage';
import { AnalyticsProducts, getAnalyticsProducts } from './getAnalyticsProducts';
import { AnalyticsUser, getAnalyticsUser } from './getAnalyticsUser';
import { logAnalyticsFieldsDegradation } from './logAnalyticsFieldsDegradation';

export type GetCianAnalyticsPageViewObjectParams = {
  applicationContext: IApplicationContext;
  clientSideState: IClientSideState;
  offersData?: IPublicDesktopV2OffersGetOffersResponse;
  offersDataByPin?: IPublicDesktopV2OffersGetOffersByPinResponse;
  queryClient: QueryClient;
  userData?: IFastFiltersUserResponseSchema83;
};

export function getCianAnalyticsPageViewObject(
  params: GetCianAnalyticsPageViewObjectParams,
): CianAnalyticsPageViewObject {
  const {
    applicationContext,
    clientSideState,
    queryClient,
    offersDataByPin,
    offersData = new FetchGetOffersLoadService(queryClient).getQueryData(),
    userData = new FetchGetUserLoadService(queryClient).getQueryData(),
  } = params;

  const state = clientSideState.getState();

  const isPin = !!offersDataByPin;

  const currentOffersData = isPin ? offersDataByPin : offersData;

  const searchConditions = isPin ? completeJsonQueryByPinSelector(state) : completeJsonQuerySelector(state);

  let page: AnalyticsPage | undefined;
  let products: AnalyticsProducts | undefined;

  if (currentOffersData) {
    page = getAnalyticsPage({
      isShortListingLayout: listingLayoutIsShortSelector(state),
      page: currentOffersData.analytics.page,
    });

    products = getAnalyticsProducts({ offers: currentOffersData.offers });
  }

  const user = ((): AnalyticsUser | undefined => {
    if (userData) {
      return getAnalyticsUser({ user: userCianApiSiteV1GetUserDataByRequestSelector(userData) });
    }

    return undefined;
  })();

  logAnalyticsFieldsDegradation(applicationContext, {
    domain: 'src/shared/utils/analytics/getCianAnalyticsPageViewObject.ts',
    isPin,
    page,
    products,
    user,
  });

  /**
   * @description Тут приводим типы, так как нужно хоть что-то вернуть для аналитики, делаем деградацию и логируем
   *   выше, если что то мы не смогли найти
   */
  return {
    action: 'newpage',
    category: 'page',
    label: 'open',
    name: 'newpage',
    page: page as AnalyticsPage,
    products: products as AnalyticsProducts,
    searchConditions,
    user: user as AnalyticsUser,
  };
}
